import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TONES = [
  { v: "expert-friendly", label: "Expert-friendly", desc: "Authoritative but warm. Default." },
  { v: "conversational", label: "Conversational", desc: "Casual, second-person, like a friend." },
  { v: "formal",         label: "Formal",         desc: "Polished, B2B, professional services." },
  { v: "edgy",           label: "Edgy",           desc: "Punchy, opinionated, no hedging." },
];

export default function PulseSiteNew() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [niche, setNiche] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [kwInput, setKwInput] = useState("");
  const [tone, setTone] = useState("expert-friendly");
  const [audience, setAudience] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (loading) return null;
  if (!user) {
    return (
      <main className="max-w-md mx-auto px-5 py-32 text-center">
        <h1 className="fl-display text-3xl font-bold">Sign in to create a Pulse site</h1>
        <Link to="/login" className="fl-btn mt-6 inline-flex">Sign in</Link>
      </main>
    );
  }

  const addKw = () => {
    const k = kwInput.trim();
    if (!k) return;
    if (keywords.length >= 25) { setErr("Max 25 keywords per site"); return; }
    if (keywords.includes(k)) { setKwInput(""); return; }
    setKeywords([...keywords, k]);
    setKwInput("");
    setErr("");
  };
  const removeKw = (k) => setKeywords(keywords.filter(x => x !== k));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !niche.trim()) { setErr("Name and niche are required"); return; }
    if (keywords.length === 0) { setErr("Add at least one keyword"); return; }
    if (!acceptedTerms) { setErr("Please accept the Pulse usage terms before creating a site"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        "/pulse/sites",
        { name: name.trim(), niche: niche.trim(), keywords, tone, target_audience: audience.trim(), accepted_terms: true },
        { headers: authHeaders() },
      );
      navigate(`/pulse/${data.site_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to create site");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-2xl mx-auto px-5 py-12" data-testid="pulse-site-new">
      <Link to="/pulse" className="text-white/50 text-sm inline-flex items-center gap-1 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Pulse
      </Link>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-xs font-semibold tracking-wide uppercase">
        <Sparkles className="w-3.5 h-3.5" /> New Pulse site
      </div>
      <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight text-white">
        What are we writing about?
      </h1>
      <p className="text-white/55 mt-2">Three minutes of setup, then Pulse can generate posts on demand.</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        <div>
          <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Site name</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Indie Radio Insider"
            className="fl-input w-full"
            data-testid="pulse-site-name"
            maxLength={80}
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Niche / topic</label>
          <input
            value={niche}
            onChange={e => setNiche(e.target.value)}
            placeholder="e.g. starting an internet radio station as a musician"
            className="fl-input w-full"
            data-testid="pulse-site-niche"
            maxLength={140}
          />
          <div className="text-xs text-white/40 mt-1">Be specific — Pulse uses this as the editorial north star.</div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Keywords ({keywords.length}/25)</label>
          <div className="flex gap-2">
            <input
              value={kwInput}
              onChange={e => setKwInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
              placeholder="how to start an internet radio station"
              className="fl-input flex-1"
              data-testid="pulse-keyword-input"
            />
            <button type="button" onClick={addKw} className="fl-btn ghost" data-testid="pulse-add-keyword">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          {keywords.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="pulse-keywords-list">
              {keywords.map(k => (
                <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-100 text-xs">
                  {k}
                  <button type="button" onClick={() => removeKw(k)} className="hover:text-white" aria-label={`Remove ${k}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="text-xs text-white/40 mt-2">Pulse rotates through these so each post targets fresh search demand.</div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Tone</label>
          <div className="grid grid-cols-2 gap-2">
            {TONES.map(t => (
              <button
                type="button"
                key={t.v}
                onClick={() => setTone(t.v)}
                className={`text-left p-3 rounded-xl border transition ${tone === t.v ? "border-fuchsia-400/60 bg-fuchsia-500/10" : "border-white/10 hover:border-white/20"}`}
                data-testid={`pulse-tone-${t.v}`}
              >
                <div className="text-white text-sm font-semibold">{t.label}</div>
                <div className="text-white/50 text-xs mt-0.5">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Target audience (optional)</label>
          <input
            value={audience}
            onChange={e => setAudience(e.target.value)}
            placeholder="e.g. indie musicians age 22-40 in the US who want to start streaming"
            className="fl-input w-full"
            data-testid="pulse-site-audience"
            maxLength={200}
          />
        </div>

        {err && <div className="text-rose-400 text-sm" data-testid="pulse-site-error">{err}</div>}

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="pulse-terms-label">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={e => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-4 h-4 accent-fuchsia-500 flex-shrink-0"
              data-testid="pulse-accept-terms"
            />
            <span className="text-xs text-white/65 leading-relaxed">
              I will publish Pulse-generated posts <strong className="text-white">only on websites I own or operate</strong>
              {" "}(my own business or personal site). I will not resell, sub-license, or offer Pulse
              output to the public as a service. I understand each post is for my own use and that
              violating these terms can result in account suspension.
            </span>
          </label>
        </div>

        <div className="text-xs leading-relaxed text-amber-200/90 bg-amber-400/5 border border-amber-400/30 rounded-lg p-3" data-testid="pulse-sharing-note">
          <strong className="text-amber-200">Please note:</strong> Pulse writes and publishes new articles automatically every day on your plan's schedule, and any website embed updates by itself. We don't share or promote your articles on social media or elsewhere; <strong className="text-white">you're responsible for sharing them</strong>.
        </div>

        <button type="submit" disabled={busy} className="fl-btn w-full sm:w-auto" data-testid="pulse-site-submit">
          <Sparkles className="w-4 h-4" /> {busy ? "Creating…" : "Create site"}
        </button>
      </form>
    </main>
  );
}
